import styled, { css } from 'styled-components';
import { brandTheme } from '../../../theme/brandTokens';
import { modalCloseButton } from '../../../theme/modalClose';

/**
 * Accounts page — mirrors routes/accounts.tsx in the Remix reference.
 *
 * Two rules keep this in step with it:
 *
 * 1. Colour comes from --hd-* tokens only. The reference styles this page with
 *    semantic classes (bg-card, text-muted-foreground, border-border), all of
 *    which resolve differently per theme. Hardcoded hex cannot do that.
 *
 * 2. Radii follow the reference's overridden scale (--radius: 0.875rem), not
 *    stock Tailwind:
 *      sm 10 · md 12 · lg 14 · xl 18 · 2xl 22 · full 999
 *    Stock values (6/8/12/16) make every card and control visibly boxier.
 */

const FG = 'var(--hd-foreground)';
const MUTED_FG = 'var(--hd-muted-foreground)';
const MUTED = 'var(--hd-muted)';
const BORDER = 'var(--hd-border)';
const CARD = 'var(--hd-card)';
const BG = 'var(--hd-background)';

/* bg-muted/40 and bg-muted/60 — the reference leans on these part-strength
   fills so a raised control still reads against the surface behind it. */
const MUTED_40 = 'color-mix(in srgb, var(--hd-muted) 40%, transparent)';
const MUTED_60 = 'color-mix(in srgb, var(--hd-muted) 60%, transparent)';
const FG_30 = 'color-mix(in srgb, var(--hd-foreground) 30%, transparent)';
/* Tailwind v4's shadow-sm — the reference runs v4, where the scale shifted:
   v3's shadow-sm is v4's shadow-xs. */
const SHADOW_SM = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)';

/* Tint pills: bg-{hue}-500/10 with text that lightens in dark. */
const EMERALD_TINT = 'color-mix(in srgb, #10b981 10%, transparent)';
const AMBER_TINT = 'color-mix(in srgb, #f59e0b 10%, transparent)';
const AMBER_TINT_HOVER = 'color-mix(in srgb, #f59e0b 15%, transparent)';
const ROSE_TINT = 'color-mix(in srgb, #f43f5e 10%, transparent)';

export const StyledAccounts = styled.div`
  ${brandTheme}
  min-height: 100%;
  background: ${BG};
  color: ${FG};

  .accounts-main {
    width: 100%;
    max-width: 1480px;
    margin: 0 auto;
    padding: 32px;

    @media (max-width: 767px) {
      padding: 24px 16px;
    }
  }

  .accounts-head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
  }

  .accounts-title {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    letter-spacing: -0.025em;
    color: ${FG};
  }

  .accounts-subtitle {
    margin: 4px 0 0;
    font-size: 14px;
    color: ${MUTED_FG};
  }

  /* The same primary button as the Campaigns page's Create campaign. */
  .accounts-cta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 36px;
    padding: 0 16px;
    border: none;
    border-radius: 18px;
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    background: var(--hd-gradient-houdiny);
    box-shadow: var(--hd-shadow-card);
    cursor: pointer;
    transition: transform 0.15s ease;

    &:hover {
      transform: scale(1.02);
    }
  }

  /* Trough stays at the reference's bg-muted/40 — barely tinted. The active tab
     is made legible by its own ring and lift below, not by darkening this. */
  .accounts-tabs {
    display: inline-flex;
    margin-top: 24px;
    padding: 4px;
    border: 1px solid ${BORDER};
    border-radius: 18px;
    background: ${MUTED_40};
  }

  .accounts-tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 16px;
    border: none;
    border-radius: 14px;
    font-size: 14px;
    font-weight: 500;
    color: ${MUTED_FG};
    background: transparent;
    cursor: pointer;
    transition: color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      color: ${FG};
    }

    /* Temporarily unavailable tab — still listed so the feature reads as
       coming back, but inert and visibly dimmed. */
    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;

      &:hover {
        color: ${MUTED_FG};
      }
    }

    /* Weight deliberately stays at 500, as in the reference. Bolding only the
       active label changes its width, which nudges the neighbouring tabs
       mid-transition and undoes the smoothness. */
    &.active {
      color: ${FG};
      background: ${BG};
      /* Hairline ring + lift. The ring is what carries the separation in dark,
         where the pill is darker than the trough and a drop shadow is invisible;
         it rides in the box-shadow so it adds no layout width. */
      box-shadow:
        0 0 0 1px ${BORDER},
        0 1px 2px rgba(15, 23, 42, 0.08),
        0 2px 6px rgba(15, 23, 42, 0.1);
    }

    /* Only the active tab's count gets a chip; on the inactive tabs it would be
       a chip on a chip, and the brighter fill pulled the eye the wrong way. */
    .tab-count {
      padding: 0 6px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 500;
      color: ${MUTED_FG};
      background: transparent;
    }

    &.active .tab-count {
      color: ${FG};
      background: ${MUTED};
    }
  }

  .accounts-section {
    margin-top: 24px;
  }

  .accounts-stack {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .stats-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;

    @media (min-width: 640px) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }

  .providers-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;

    @media (min-width: 768px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  .accounts-search {
    display: flex;
    align-items: center;
    gap: 8px;

    > div,
    .form-item,
    .form-group {
      flex: 1;
      min-width: 0;
      margin: 0;
    }

    input {
      flex: 1;
      width: 100%;
      height: 36px;
      padding: 0 12px;
      border: 1px solid ${BORDER};
      border-radius: 14px;
      font-size: 14px;
      color: ${FG};
      background: ${BG};
      outline: none;

      &::placeholder {
        color: ${MUTED_FG};
      }

      &:focus {
        border-color: ${FG_30};
      }
    }

    button {
      height: 36px;
      padding: 0 14px;
      border: 1px solid ${BORDER};
      border-radius: 14px;
      font-size: 14px;
      font-weight: 500;
      color: ${FG};
      background: ${CARD};
      cursor: pointer;

      &:hover {
        background: ${MUTED};
      }
    }
  }

  .accounts-empty {
    padding: 48px 24px;
    border: 1px solid ${BORDER};
    border-radius: 22px;
    text-align: center;
    font-size: 14px;
    color: ${MUTED_FG};
    background: ${CARD};
  }

  .domain-back {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    padding: 0;
    border: none;
    font-size: 14px;
    font-weight: 500;
    color: ${FG};
    background: transparent;
    cursor: pointer;

    &:hover {
      opacity: 0.75;
    }
  }

  .accounts-inline-cta {
    display: flex;
    justify-content: flex-end;
  }
`;

export const StyledStatCard = styled.div`
  padding: 16px;
  border: 1px solid ${BORDER};
  border-radius: 22px;
  background: ${CARD};

  .stat-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: ${MUTED_FG};
  }

  .stat-value {
    margin-top: 8px;
    font-size: 24px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${FG};
  }
`;

export const StyledProviderCard = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px;
  border: 1px solid ${BORDER};
  border-radius: 22px;
  text-align: left;
  background: ${CARD};
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${FG_30};

    .provider-plus {
      color: ${FG};
    }
  }

  .provider-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .provider-icon {
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    border-radius: 18px;
    font-size: 14px;
    font-weight: 600;
    color: ${FG};
    background: ${MUTED};
  }

  /* Not connectable yet: dimmed, and the hover treatment withdrawn so the card
     stops inviting a click it will not answer. The tag keeps full contrast — it
     is the one thing on the card worth reading. */
  &.is-soon {
    cursor: not-allowed;

    /* The icon and the strapline recede; the name and its tag do not. Dimming
       the whole left side would have taken the tag with it, since it sits
       inside — and the tag is the one thing on the card worth reading. */
    .provider-icon,
    .provider-desc {
      opacity: 0.5;
    }

    .provider-name {
      color: ${MUTED_FG};
    }

    &:hover {
      border-color: ${BORDER};
    }
  }

  .provider-name {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
    color: ${FG};
  }

  .provider-soon {
    flex-shrink: 0;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 9.5px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
    color: ${MUTED_FG};
    background: ${MUTED};
  }

  .provider-desc {
    margin-top: 2px;
    font-size: 12px;
    color: ${MUTED_FG};
  }

  .provider-plus {
    color: ${MUTED_FG};
    transition: color 0.15s ease;
  }
`;

export const StyledAccountsTable = styled.div`
  overflow-x: auto;
  border: 1px solid ${BORDER};
  border-radius: 22px;
  background: ${CARD};

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
  }

  /* Placeholder rows keep the real row's height so nothing jumps when the
     data arrives. */
  tbody tr.skeleton-row td {
    height: 61px;
  }

  .accounts-pager {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding: 12px 16px;
    border-top: 1px solid ${BORDER};

    .pager-range {
      font-size: 12px;
      color: ${MUTED_FG};
    }

    /* The shared Pagination molecule ships its own list spacing; only the
       alignment inside this footer needs setting. */
    ul {
      margin: 0;
      justify-content: flex-end;
    }
  }

  thead {
    background: ${MUTED_40};
    color: ${MUTED_FG};
    font-size: 12px;
    font-weight: 500;
    /* tracking-wide */
    letter-spacing: 0.025em;
    text-transform: uppercase;

    th {
      padding: 12px 16px;
      text-align: left;
      font-weight: 500;
    }
  }

  tbody tr {
    border-top: 1px solid ${BORDER};
  }

  td {
    padding: 12px 16px;
    vertical-align: middle;
    color: ${FG};
  }

  .mailbox-name {
    font-weight: 500;
  }

  .mailbox-email {
    margin-top: 2px;
    font-size: 12px;
    color: ${MUTED_FG};
  }

  .muted {
    color: ${MUTED_FG};
  }

  .sent-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    font-variant-numeric: tabular-nums;
  }

  .sent-bar {
    width: 80px;
    height: 6px;
    overflow: hidden;
    border-radius: 999px;
    background: ${MUTED};
  }

  /* bg-foreground/80 */
  .sent-fill {
    height: 100%;
    background: color-mix(in srgb, var(--hd-foreground) 80%, transparent);
  }

  .warmup-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    font-variant-numeric: tabular-nums;
  }

  .row-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    color: ${MUTED_FG};
  }

  .action-chip,
  .icon-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    border: none;
    border-radius: 14px;
    color: inherit;
    background: transparent;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover {
      color: ${FG};
      background: ${MUTED};
    }
  }

  .action-chip {
    gap: 6px;
    padding: 0 10px;
    font-size: 12px;
    font-weight: 500;

    &.warming {
      color: var(--hd-accent-amber);
      background: ${AMBER_TINT};

      &:hover {
        color: var(--hd-accent-amber);
        background: ${AMBER_TINT_HOVER};
      }
    }
  }

  .icon-action {
    width: 32px;
    padding: 0;
  }
`;

export const StyledStatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;

  ${({ $status }) =>
    $status === 'active' &&
    css`
      color: var(--hd-accent-emerald);
      background: ${EMERALD_TINT};
    `}

  ${({ $status }) =>
    $status === 'warming' &&
    css`
      color: var(--hd-accent-amber);
      background: ${AMBER_TINT};
    `}

  ${({ $status }) =>
    $status === 'paused' &&
    css`
      color: ${MUTED_FG};
      background: ${MUTED};
    `}

  ${({ $status }) =>
    $status === 'error' &&
    css`
      color: var(--hd-accent-rose);
      background: ${ROSE_TINT};
    `}
`;

export const StyledLinkedinLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: 1024px) {
    grid-template-columns: 280px minmax(0, 1fr);
  }

  .li-sidebar {
    padding: 16px;
    border: 1px solid ${BORDER};
    border-radius: 22px;
    background: ${CARD};
  }

  .li-sidebar-title {
    padding: 0 4px 12px;
    font-size: 14px;
    font-weight: 600;
    color: ${FG};
  }

  .li-signin {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    margin-bottom: 12px;
    padding: 8px 12px;
    border: none;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    /* Brand blue stays literal: it is a filled button behind white text, so it
       must not lighten with the theme. */
    background: #0a66c2;
    box-shadow: ${SHADOW_SM};
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: #004182;
    }
  }

  .li-profile-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .li-profile-btn {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 8px;
    border: none;
    border-radius: 18px;
    text-align: left;
    font-size: 14px;
    color: ${FG};
    background: transparent;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: ${MUTED_60};
    }

    &.active {
      background: ${MUTED};
    }
  }

  .li-profile-name {
    flex: 1;
    overflow: hidden;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .li-status-dot {
    width: 8px;
    height: 8px;
    flex-shrink: 0;
    border-radius: 50%;
    /* bg-muted-foreground/50 — the paused default. */
    background: color-mix(in srgb, var(--hd-muted-foreground) 50%, transparent);

    &.active {
      background: #10b981;
    }

    &.warming {
      background: #f59e0b;
    }

    &.error {
      background: #f43f5e;
    }
  }

  .li-detail {
    display: flex;
    flex-direction: column;
    gap: 24px;
    min-width: 0;
  }

  .li-limits-title {
    margin: 0 0 12px;
    font-size: 18px;
    font-weight: 600;
    color: ${FG};
  }
`;

export const StyledLinkedinCard = styled.div`
  overflow: hidden;
  border: 1px solid ${BORDER};
  border-radius: 22px;
  background: ${CARD};
  color: ${FG};
  box-shadow: ${SHADOW_SM};

  .li-banner {
    position: relative;
    height: 140px;
    width: 100%;
    background: linear-gradient(135deg, #a0b4c8 0%, #cbd6e2 100%) center / cover no-repeat;
  }

  .li-banner-overlay {
    position: absolute;
    inset: 0;
    opacity: 0.3;
    mix-blend-mode: overlay;
    background-image: radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.5), transparent 45%),
      radial-gradient(circle at 80% 60%, rgba(255, 255, 255, 0.35), transparent 50%);
  }

  /* Sits on the coloured banner in both themes, so it keeps its literal
     white-on-slate treatment. */
  .li-banner-status {
    position: absolute;
    top: 12px;
    right: 12px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    color: #334155;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    box-shadow: ${SHADOW_SM};
  }

  .li-body {
    position: relative;
    padding: 0 24px 20px;
  }

  .li-avatar-row {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    margin-top: -56px;
    flex-wrap: wrap;
  }

  .li-avatar-ring {
    padding: 4px;
    border-radius: 50%;
    background: ${CARD};
    box-shadow: ${SHADOW_SM};
    outline: 1px solid ${BORDER};
  }

  .li-card-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
    flex-wrap: wrap;
  }

  .li-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 12px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;
  }

  .li-btn-ghost {
    border: 1px solid ${BORDER};
    color: ${FG};
    background: ${BG};

    &:hover {
      background: ${MUTED};
    }
  }

  .li-btn-outline {
    border: 1px solid var(--hd-linkedin);
    color: var(--hd-linkedin);
    background: transparent;

    &:hover {
      background: color-mix(in srgb, var(--hd-linkedin) 5%, transparent);
    }
  }

  .li-btn-primary {
    border: none;
    color: #fff;
    background: #0a66c2;

    &:hover {
      background: #004182;
    }
  }

  .li-name-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
    flex-wrap: wrap;
  }

  .li-name {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: -0.025em;
    color: ${FG};
  }

  .li-headline {
    margin: 4px 0 0;
    max-width: 42rem;
    font-size: 14px;
    /* leading-snug */
    line-height: 1.375;
    color: ${MUTED_FG};
  }

  .li-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
    margin-top: 8px;
    font-size: 12.5px;
    color: ${MUTED_FG};
  }

  .li-link {
    padding: 0;
    border: none;
    font: inherit;
    font-weight: 600;
    color: var(--hd-linkedin);
    background: transparent;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .li-session {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
    padding: 8px 12px;
    border: 1px solid ${BORDER};
    border-radius: 18px;
    font-size: 12.5px;
    background: ${MUTED_40};
    color: ${MUTED_FG};
  }

  .li-schedule {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid ${BORDER};
  }

  .li-schedule-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: ${FG};
  }

  .li-schedule-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 10px 0 0;
    padding: 0;
    list-style: none;
  }

  .li-schedule-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 16px;
    font-size: 12.5px;
    color: ${MUTED_FG};
  }

  .li-schedule-days {
    display: flex;
    gap: 4px;

    span {
      display: grid;
      place-items: center;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      font-size: 11px;
      font-weight: 600;
      background: ${MUTED};
      color: ${MUTED_FG};
    }

    span.on {
      background: var(--hd-primary);
      color: #fff;
    }
  }

  .li-schedule-hours {
    font-weight: 500;
    color: ${FG};
  }

  .li-schedule-tz {
    margin-left: 6px;
    font-weight: 400;
    color: ${MUTED_FG};
  }

  .li-schedule-campaign {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
    color: ${MUTED_FG};
    text-decoration: none;

    &:hover {
      color: var(--hd-primary);
    }
  }

  .li-schedule-empty {
    margin: 8px 0 0;
    font-size: 12.5px;
    color: ${MUTED_FG};
  }

  .li-connect-banner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 16px;
    padding: 14px 16px;
    border: 1px solid color-mix(in srgb, #0a66c2 28%, ${BORDER});
    border-radius: 16px;
    background: color-mix(in srgb, #0a66c2 7%, ${CARD});

    p {
      margin: 0;
      flex: 1 1 180px;
      font-size: 13px;
      line-height: 1.4;
      color: ${MUTED_FG};
    }

    .li-btn-primary {
      flex-shrink: 0;
    }
  }

  .li-live {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    color: var(--hd-accent-emerald);
    background: ${EMERALD_TINT};
  }

  .manage-wrap {
    position: relative;
  }

  .manage-menu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 5;
    min-width: 160px;
    padding: 4px;
    border: 1px solid ${BORDER};
    border-radius: 18px;
    background: ${CARD};
    box-shadow: var(--hd-shadow-elevated);
  }

  .manage-item {
    display: block;
    width: 100%;
    padding: 8px 12px;
    border: none;
    border-radius: 14px;
    text-align: left;
    font-size: 13px;
    color: var(--hd-destructive);
    background: transparent;
    cursor: pointer;

    &:hover {
      background: color-mix(in srgb, var(--hd-destructive) 8%, transparent);
    }
  }
`;

export const StyledLiAvatar = styled.div`
  display: grid;
  place-items: center;
  overflow: hidden;
  border-radius: 50%;
  font-weight: 600;
  color: #fff;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const StyledLimitsTable = styled.div`
  overflow-x: auto;
  border: 1px solid ${BORDER};
  border-radius: 22px;
  background: ${CARD};

  .limits-head,
  .limits-row {
    display: grid;
    grid-template-columns: 1.5fr 1fr 1fr 1fr 1fr;
    gap: 8px;
    align-items: center;
    padding: 16px 20px;
    min-width: 720px;
  }

  .limits-head {
    padding: 12px 20px;
    border-bottom: 1px solid ${BORDER};
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.025em;
    text-transform: uppercase;
    color: ${MUTED_FG};
  }

  .limits-row {
    border-bottom: 1px solid ${BORDER};
    font-size: 14px;
    color: ${FG};

    &:last-child {
      border-bottom: 0;
    }
  }

  .limits-muted {
    color: ${MUTED_FG};
    font-variant-numeric: tabular-nums;
  }

  .limits-input,
  .limits-select {
    height: 36px;
    border: 1px solid ${BORDER};
    border-radius: 14px;
    font-size: 14px;
    color: ${FG};
    background: ${BG};
    outline: none;
    font-variant-numeric: tabular-nums;
    transition: border-color 0.15s ease;

    &:focus {
      border-color: ${FG_30};
    }
  }

  .limits-input {
    width: 80px;
    padding: 0 12px;
  }

  .limits-select-wrap {
    position: relative;
    display: inline-block;
  }

  .limits-select {
    width: 112px;
    padding: 0 32px 0 12px;
    appearance: none;
  }

  .limits-chevron {
    position: absolute;
    top: 50%;
    right: 8px;
    pointer-events: none;
    color: ${MUTED_FG};
    transform: translateY(-50%);
  }

`;

/**
 * Save row. A sibling of the limits card, not a child of it — the reference
 * closes the bordered container after the last row and puts the button on its
 * own `mt-4 flex justify-end` line underneath.
 */
export const StyledLimitsSave = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;

  .save-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    padding: 0 16px;
    border: none;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 500;
    color: ${BG};
    background: ${FG};
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover:not(:disabled) {
      opacity: 0.9;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
`;

/**
 * LinkedIn sign-in modal — one stage on screen at a time.
 *
 * Deliberately narrower and plainer than the rest of the page: it is a
 * credential prompt standing in for LinkedIn's own, so it keeps LinkedIn's
 * brand blue and card proportions rather than the accounts card language.
 */
export const StyledLinkedinSignIn = styled.div`
  position: relative;
  padding: 28px;
  color: ${FG};

  &.li-si-enter {
    animation: li-si-in 0.28s ease-out;
  }

  @keyframes li-si-in {
    from {
      opacity: 0;
      transform: translateY(8px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &.li-si-enter {
      animation: none;
    }
  }

  .li-si-close {
    position: absolute;
    top: 14px;
    right: 14px;
    display: grid;
    place-items: center;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 50%;
    color: ${MUTED_FG};
    background: transparent;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover {
      color: ${FG};
      background: ${MUTED_60};
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }

  .li-si-mark {
    display: grid;
    place-items: center;
    width: 48px;
    height: 48px;
    margin: 0 auto 14px;
    border-radius: 14px;
    /* Brand blue behind a white glyph — must not follow the theme. */
    background: linear-gradient(145deg, #0a66c2 0%, #004182 100%);
    color: #fff;
    box-shadow: 0 8px 20px rgba(10, 102, 194, 0.28);
  }

  .li-si-step {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin: 0 0 14px;
  }

  .li-si-step-dot {
    display: grid;
    place-items: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    font-size: 11px;
    font-weight: 700;
    color: ${MUTED_FG};
    background: ${MUTED_40};
    border: 1px solid ${BORDER};

    &.is-active {
      color: #fff;
      background: #0a66c2;
      border-color: #0a66c2;
    }
  }

  .li-si-step-line {
    width: 28px;
    height: 2px;
    border-radius: 1px;
    background: ${BORDER};
  }

  .li-si-title {
    margin: 0;
    font-size: 18px;
    font-weight: 650;
    letter-spacing: -0.01em;
    line-height: 1.3;
    text-align: center;

    &.is-start {
      margin: 0 28px 20px 0;
      text-align: left;
      font-size: 20px;
    }
  }

  &.li-si-country-modal {
    overflow: visible;

    .li-si-required {
      display: block;
      margin: 0 0 8px;
      font-size: 13px;
      font-weight: 500;
      color: ${MUTED_FG};

      span {
        color: #e11d48;
      }
    }

    .li-si-country {
      position: relative;
      z-index: 2;
    }

    .li-si-select {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      width: 100%;
      min-height: 44px;
      padding: 0 12px;
      border: 1px solid ${BORDER};
      border-radius: 10px;
      font-size: 14px;
      color: ${FG};
      background: ${CARD};
      cursor: pointer;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;

      svg {
        flex-shrink: 0;
        color: ${MUTED_FG};
        transition: transform 0.2s ease;
      }

      &.is-open {
        border-color: #0a66c2;
        box-shadow: 0 0 0 1px #0a66c2;

        svg {
          transform: rotate(180deg);
        }
      }

      &:hover:not(:disabled) {
        border-color: color-mix(in srgb, var(--hd-foreground) 22%, transparent);
      }

      &.is-open:hover:not(:disabled) {
        border-color: #0a66c2;
      }

      &:disabled {
        opacity: 0.6;
        cursor: default;
      }
    }

    .li-si-select-placeholder {
      color: ${MUTED_FG};
    }

    .li-si-select-value {
      color: ${FG};
    }

    .li-si-select-menu {
      position: absolute;
      top: calc(100% + 6px);
      left: 0;
      right: 0;
      z-index: 40;
      overflow: hidden;
      border: 1px solid ${BORDER};
      border-radius: 10px;
      background: ${CARD};
      box-shadow: 0 10px 28px rgba(15, 23, 42, 0.1);
    }

    .li-si-menu-search {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 12px;
      border-bottom: 1px solid ${BORDER};
      color: ${MUTED_FG};

      input {
        width: 100%;
        height: 28px;
        padding: 0;
        border: none;
        border-radius: 0;
        font-size: 14px;
        color: ${FG};
        background: transparent;
        box-shadow: none;

        &:focus {
          outline: none;
          border: none;
          box-shadow: none;
        }

        &::placeholder {
          color: ${MUTED_FG};
        }
      }
    }

    .li-si-select-options {
      margin: 0;
      padding: 4px 0 6px;
      max-height: 220px;
      overflow-y: auto;
      list-style: none;
    }

    .li-si-select-option {
      display: block;
      width: 100%;
      padding: 10px 14px;
      border: none;
      font-size: 14px;
      text-align: left;
      color: ${FG};
      background: transparent;
      cursor: pointer;

      &:hover,
      &:focus-visible {
        background: ${MUTED};
      }

      &.is-selected {
        font-weight: 600;
        background: ${MUTED_40};
      }
    }

    .li-si-select-empty {
      padding: 12px 14px;
      font-size: 13px;
      color: ${MUTED_FG};
    }

    .li-si-note {
      margin-top: 12px;
      font-size: 12px;
      line-height: 1.45;
    }
  }

  .li-si-hint {
    margin: 8px 0 18px;
    font-size: 13px;
    line-height: 1.5;
    text-align: center;
    color: ${MUTED_FG};
  }

  .li-si-preview {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 0 0 16px;
    padding: 12px 14px;
    border: 1px solid ${BORDER};
    border-radius: 14px;
    background: ${MUTED_40};
  }

  .li-si-preview-flag {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 52px;
    height: 40px;
    border-radius: 8px;
    overflow: hidden;
    background: ${CARD};
    border: 1px solid ${BORDER};
    box-shadow: ${SHADOW_SM};

    .li-si-flag-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .li-si-preview-copy {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    strong {
      font-size: 14px;
      font-weight: 650;
      color: ${FG};
    }

    span {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: ${MUTED_FG};
    }
  }

  .li-si-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 0 0 14px;
  }

  .li-si-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 10px;
    border: 1px solid ${BORDER};
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: ${FG};
    background: ${CARD};
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;

    &:hover:not(:disabled) {
      border-color: color-mix(in srgb, #0a66c2 45%, ${BORDER});
      background: rgba(10, 102, 194, 0.06);
    }

    &.is-selected {
      color: #0a66c2;
      border-color: #0a66c2;
      background: rgba(10, 102, 194, 0.1);
      box-shadow: 0 0 0 2px rgba(10, 102, 194, 0.12);
    }

    &:disabled {
      opacity: 0.55;
      cursor: default;
    }
  }

  .li-si-choice {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    margin-top: 10px;
    padding: 14px;
    border: 1px solid ${BORDER};
    border-radius: 14px;
    text-align: left;
    color: ${FG};
    background: ${CARD};
    cursor: pointer;
    transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;

    &:hover:not(:disabled) {
      border-color: color-mix(in srgb, #0a66c2 40%, ${BORDER});
      background: rgba(10, 102, 194, 0.04);
    }

    &.is-primary {
      border-color: #0a66c2;
      background: rgba(10, 102, 194, 0.06);

      .li-si-choice-icon {
        color: #fff;
        background: #0a66c2;
      }
    }

    &:disabled {
      opacity: 0.55;
      cursor: default;
    }
  }

  .li-si-choice-icon {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    color: #0a66c2;
    background: rgba(10, 102, 194, 0.1);
  }

  .li-si-choice-copy {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    strong {
      font-size: 14px;
      font-weight: 650;
    }

    small {
      font-size: 12px;
      line-height: 1.35;
      color: ${MUTED_FG};
    }
  }

  label {
    display: block;
    margin: 4px 0 6px;
    font-size: 12px;
    font-weight: 500;
    color: ${MUTED_FG};
  }

  input {
    width: 100%;
    height: 40px;
    padding: 0 12px;
    border: 1px solid ${BORDER};
    border-radius: 10px;
    font-size: 15px;
    color: ${FG};
    background: ${CARD};

    &:focus {
      outline: none;
      border-color: #0a66c2;
      box-shadow: 0 0 0 1px #0a66c2;
    }

    &.code {
      text-align: center;
      font-size: 20px;
      font-weight: 600;
      letter-spacing: 0.3em;
    }
  }

  .li-si-note {
    margin: 8px 0 0;
    font-size: 11px;
    line-height: 1.45;
    color: ${MUTED_FG};
  }

  .li-si-country {
    width: 100%;
  }

  .li-si-flag {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-width: 0;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .li-si-flag-img {
    flex-shrink: 0;
    object-fit: cover;
    border-radius: 3px;
    box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.08);
  }

  .li-si-primary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    height: 46px;
    margin-top: 18px;
    border: none;
    border-radius: 23px;
    font-size: 15px;
    font-weight: 650;
    color: #fff;
    background: linear-gradient(180deg, #0a66c2 0%, #0857a6 100%);
    box-shadow: 0 8px 18px rgba(10, 102, 194, 0.28);
    cursor: pointer;
    transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;

    &:hover:not(:disabled) {
      background: linear-gradient(180deg, #0b6fd4 0%, #004182 100%);
      box-shadow: 0 10px 22px rgba(10, 102, 194, 0.34);
    }

    &:active:not(:disabled) {
      transform: translateY(1px);
    }

    &:disabled {
      opacity: 0.65;
      cursor: default;
      box-shadow: none;
    }

    /* The long "this is taking a while" wording is said on the button itself,
       where people are already looking - a line appearing underneath moves the
       button just as they reach for it. It needs two lines on a phone, so the
       button stops being a fixed height while it is showing. */
    &.is-wrapping {
      height: auto;
      min-height: 46px;
      padding: 10px 16px;
      line-height: 1.35;
      white-space: normal;
    }
  }

  .li-si-btn-spin {
    animation: li-si-spin 0.8s linear infinite;
  }

  .li-si-secondary {
    width: 100%;
    height: 42px;
    margin-top: 10px;
    border: 1px solid ${BORDER};
    border-radius: 21px;
    font-size: 15px;
    font-weight: 600;
    color: ${FG};
    background: ${CARD};
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled) {
      background: ${MUTED_60};
    }

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  .li-si-link {
    display: block;
    width: 100%;
    margin-top: 8px;
    border: none;
    font-size: 12px;
    color: ${MUTED_FG};
    background: none;
    text-decoration: underline;
    cursor: pointer;
  }

  /* Waiting stages: spinner, headline, one line of what to do next. */
  .li-si-box {
    padding: 16px;
    border: 1px solid ${BORDER};
    border-radius: 14px;
    text-align: center;
    background: ${MUTED_40};

    b {
      display: block;
      margin-top: 8px;
      font-size: 14px;
    }

    small {
      display: block;
      margin-top: 4px;
      font-size: 12px;
      color: ${MUTED_FG};
    }
  }

  .li-si-spinner {
    width: 22px;
    height: 22px;
    margin: 0 auto;
    border: 3px solid ${BORDER};
    border-top-color: #0a66c2;
    border-radius: 50%;
    animation: li-si-spin 0.8s linear infinite;
  }

  @keyframes li-si-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .li-si-done {
    padding: 8px 0;
    text-align: center;

    .name {
      margin-top: 10px;
      font-weight: 600;
    }

    small {
      color: ${MUTED_FG};
    }
  }

  /* Two ways out of a stage that has stopped moving - open the live browser, or
     keep waiting. Side by side because neither is the obvious answer; the button
     margins that stack them in a form would read as a sequence here. */
  .li-si-box-actions {
    display: flex;
    gap: 10px;
    width: 100%;
    margin-top: 14px;

    button {
      flex: 1;
      margin-top: 0;
    }
  }

  /* Not a password field, on purpose. Chrome hangs its saved-password menu on any
     type="password" input on the origin - which here would offer the Tigaly
     login for a LinkedIn box - and no attribute turns that off. A text input
     masked in CSS gets no menu, no generated password and no "save password?".
     LinkedinSignInSteps only sets this where the browser supports the masking;
     everywhere else the field stays a real password input. */
  .li-si-masked {
    -webkit-text-security: disc;
  }

  .li-si-slow {
    margin: 8px 0 0;
    font-size: 12px;
    text-align: center;
    color: ${MUTED_FG};
  }

  .li-si-err {
    margin: 10px 0 0;
    min-height: 16px;
    font-size: 13px;
    text-align: center;
    color: #b91c1c;
  }
  /* Same close button as every other modal (theme/modalClose). */
  .li-si-close {
    ${modalCloseButton}
  }

`;

/** Portaled country menu — rendered on document.body, above modal overflow. */
export const StyledCountryMenuPortal = styled.div`
  overflow: hidden;
  border: 1px solid ${BORDER};
  border-radius: 10px;
  background: ${CARD};
  color: ${FG};
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.1);

  .li-si-menu-search {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-bottom: 1px solid ${BORDER};
    color: ${MUTED_FG};

    input {
      width: 100%;
      height: 28px;
      padding: 0;
      border: none;
      border-radius: 0;
      font-size: 14px;
      color: ${FG};
      background: transparent;
      box-shadow: none;

      &:focus {
        outline: none;
        border: none;
        box-shadow: none;
      }

      &::placeholder {
        color: ${MUTED_FG};
      }
    }
  }

  .li-si-select-options {
    margin: 0;
    padding: 4px 0 6px;
    max-height: 220px;
    overflow-y: auto;
    list-style: none;
  }

  .li-si-select-option {
    display: block;
    width: 100%;
    padding: 10px 14px;
    border: none;
    font-size: 14px;
    text-align: left;
    color: ${FG};
    background: transparent;
    cursor: pointer;

    &:hover,
    &:focus-visible {
      background: ${MUTED};
    }

    &.is-selected {
      font-weight: 600;
      background: ${MUTED_40};
    }
  }

  .li-si-select-empty {
    padding: 12px 14px;
    font-size: 13px;
    color: ${MUTED_FG};
  }
`;
